import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { AppConfig } from './config'
import { loadConfig } from './config'
import { WalletApi } from './api'

/*
  Exported for the browser-test harness (#151), which mounts the real component
  tree with a stubbed API and session. Those are the INPUTS to a layout test;
  everything below them is the subject.

  Exported rather than re-implemented on purpose: seven browser-only defects in
  one day each needed a harness built by hand and thrown away, and a copy of a
  provider is a copy that drifts from the thing it is standing in for.

  `browser.html` is served in development and never built, so nothing that reads
  this reaches `dist/` — `check-invariants.sh` fails the run if it ever does.
*/
// eslint-disable-next-line react-refresh/only-export-components
export const ConfigContext = createContext<AppConfig | null>(null)
// eslint-disable-next-line react-refresh/only-export-components
export const ApiContext = createContext<WalletApi | null>(null)

type State =
  | { status: 'loading' }
  | { status: 'ready'; config: AppConfig; api: WalletApi }
  | { status: 'failed'; error: Error }

/**
 * Boots the app.
 *
 * Nothing renders before `/config.json` has been fetched and validated. That is
 * the point of backend CLAUDE.md §5b — required config, no default, refuse to
 * start without it. Rendering a shell first and filling the API base in later
 * would mean a window in which a component could fire a request at a URL that
 * does not exist, and a fallback would mean it fires at the wrong one.
 */
export function ConfigProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    loadConfig()
      .then((config) => {
        if (cancelled) return
        setState({ status: 'ready', config, api: new WalletApi(config) })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setState({ status: 'failed', error: error instanceof Error ? error : new Error(String(error)) })
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (state.status === 'loading') {
    return (
      <div className="boot" role="status" aria-live="polite">
        <span className="boot__mark">TESORA</span>
      </div>
    )
  }

  if (state.status === 'failed') {
    return (
      <div className="boot boot--failed" role="alert">
        <span className="boot__mark">TESORA</span>
        <h1>This deployment is not configured.</h1>
        <p>{state.error.message}</p>
        <p className="boot__hint">
          The container renders <code>/config.json</code> from its <code>TESORA_*</code>{' '}
          environment variables at start-up. If that file is missing or incomplete the app stops
          here rather than guessing which backend to call.
        </p>
      </div>
    )
  }

  return (
    <ConfigContext.Provider value={state.config}>
      <ApiContext.Provider value={state.api}>{children}</ApiContext.Provider>
    </ConfigContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfig(): AppConfig {
  const c = useContext(ConfigContext)
  if (c === null) throw new Error('useConfig must be used inside a ConfigProvider')
  return c
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApi(): WalletApi {
  const a = useContext(ApiContext)
  if (a === null) throw new Error('useApi must be used inside a ConfigProvider')
  return a
}
